"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CONTEXT_PROVIDERS = exports.MERGE_CONTEXT_LABELS = exports.MERGE_CONTEXTS = void 0;
exports.resolvableIn = resolvableIn;
exports.classifyField = classifyField;
/** The kind of subject a document is bound to. */
exports.MERGE_CONTEXTS = [
    'deal_software', 'deal_hardware', 'checkout', 'tenant_onboarding',
    'location', 'employment', 'partner', 'vendor', 'website',
];
/** Plain-English label per context, for the document's context picker. */
exports.MERGE_CONTEXT_LABELS = {
    deal_hardware: 'Hardware deal',
    deal_software: 'Software deal',
    checkout: 'Checkout',
    tenant_onboarding: 'Tenant onboarding',
    location: 'Location',
    employment: 'Employment',
    partner: 'Partner / ISO',
    vendor: 'Vendor / supplier',
    website: 'Website',
};
/**
 * WHICH PROVIDERS A CONTEXT HAS. This is the ONLY declaration of a field's
 * availability — a `contexts` array on the field itself would encode the same
 * fact twice, and two encodings of one fact is the drift this engine exists to
 * eliminate.
 *
 * `website` is still a deliberate placeholder — it has no counterparty of its
 * own, only us and the dates.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE EMPLOYER IS THE TENANT, NOT US.
 *
 * `employment` deliberately omits `company`. `company` is the PLATFORM (Ruby
 * Till, from the Company Identity Master); `customer` is the TENANT. An
 * employment agreement is between a merchant and their own staff member, so
 * its employer side is `customer`. This row previously listed `company` and not
 * `customer`, which meant `{{company_legal_name}}` in an employment agreement
 * would render "Ruby Till LLC" as the employer of a merchant's barista — a
 * signed contract naming the wrong legal entity as a party. Omitting the
 * provider makes that unauthorable rather than merely discouraged.
 *
 * `vendor` and `partner` keep `company`, because on those two we genuinely are
 * the contracting party.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * A HARDWARE DEAL CARRIES `package` TOO.
 *
 * `deal_hardware` deliberately has no `device`/`package` symmetry with
 * `deal_software`, because the split is about what the document may SAY, not
 * about two separate orders. A deal is ONE order: it names `deviceSkuIds` and
 * `packageIds` together, and the envelope creator for a deal's hardware
 * agreement composes `packageValues` onto the same merge map as the device and
 * finance values (billing-service `onboarding.ts`, "THE SOFTWARE ON THE SAME
 * CHECKOUT"). Listing every provider the renderer actually supplies is the
 * whole contract of this map — omitting `package` made the deal publish gate
 * refuse `{{package_name_list}}` on a device purchase agreement that renders
 * it correctly at signing.
 *
 * `deal_software` still omits the hardware providers: a software-only order
 * has no devices to describe, so that direction is a real absence.
 */
exports.CONTEXT_PROVIDERS = {
    deal_hardware: ['company', 'customer', 'signer', 'guarantor', 'equipment', 'device', 'finance', 'package', 'deal', 'agreement'],
    deal_software: ['company', 'customer', 'signer', 'package', 'deal', 'agreement'],
    checkout: ['company', 'customer', 'signer', 'equipment', 'device', 'package', 'agreement'],
    tenant_onboarding: ['company', 'customer', 'signer', 'package', 'deal', 'agreement'],
    location: ['company', 'customer', 'signer', 'agreement'],
    employment: ['customer', 'employee', 'signer', 'agreement'],
    partner: ['company', 'partner', 'signer', 'agreement'],
    vendor: ['company', 'vendor', 'signer', 'agreement'],
    website: ['company', 'agreement'],
};
function resolvableIn(field, ctx) {
    return exports.CONTEXT_PROVIDERS[ctx].includes(field.resolve.provider);
}
/**
 * STRUCTURAL WINS. A field whose provider is absent from the context is a
 * configuration error whether or not it also declares an ask — asking a
 * customer for a value the document should never have referenced is not a fix.
 */
function classifyField(field, ctx) {
    if (!resolvableIn(field, ctx)) {
        return {
            kind: 'structural',
            reason: `"${field.name}" comes from the ${field.resolve.provider} provider, `
                + `which is not available in a ${ctx} document.`,
        };
    }
    if (field.ask)
        return { kind: 'ask', spec: field.ask };
    return { kind: 'deferred' };
}
//# sourceMappingURL=context.js.map